import { TOKEN_2022_PROGRAM_ID_STRING, TOKEN_PROGRAM_ID_STRING } from "@solomon-labs/constants";
import { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";

/**
 * Solana address verification — three layered checks ordered
 * cheapest → most authoritative.
 *
 * | Tier | Method        | Where | Cost   | Catches                                |
 * |------|---------------|-------|--------|----------------------------------------|
 * | 1    | isShape       | local | ~10µs  | malformed base58, truncated paste      |
 * | 2    | isWallet      | local | <1ms   | off-curve bytes (PDAs and garbage)     |
 * | 3    | fetchAccount  | RPC   | ~200ms | account existence, owner, balance      |
 *
 * Tier 1 and 2 are safe to run on every keystroke (combined cost is
 * sub-millisecond). Tier 3 should be debounced ~300–500ms after the
 * user stops typing, and only fired when Tier 1+2 pass — no point
 * asking the chain about input that's obviously malformed.
 *
 * # On-curve semantics
 *
 * Solana uses Ed25519. ~50% of all 32-byte sequences correspond to
 * actual points on the curve; the other ~50% are mathematically
 * invalid keys.
 *
 *   - **On-curve**  → could be a wallet. Private keys necessarily
 *                     produce curve points, so real wallets always
 *                     land on the curve.
 *   - **Off-curve** → no private key can exist. This is the *defining
 *                     property* of Program Derived Addresses (PDAs,
 *                     Associated Token Accounts, vault accounts) —
 *                     they're intentionally derived to be off-curve
 *                     so program logic alone controls them.
 *
 * Use `isWallet` when the destination must be a user wallet (rewards
 * destinations, send-to-friend fields). Use `isShape` when PDAs are
 * legitimate inputs (vault addresses, arbitrary program accounts).
 *
 * # Why three tiers
 *
 * Each tier catches a class of failure the previous can't:
 *   - Tier 1 catches "this clearly isn't a Solana address" (wrong
 *     charset, truncated paste, wrong length after decode).
 *   - Tier 2 catches "this has the right shape but is mathematically
 *     not a wallet" (garbled paste that survived Tier 1 because it
 *     happened to decode to 32 bytes; or a PDA, which is a real
 *     address but not the kind we want here).
 *   - Tier 3 catches "this is a structurally valid address but the
 *     account on chain isn't what we expect" (wrong-mint token
 *     account, account owned by an unknown program, etc.).
 *
 * Tier 3 is the only authoritative tier — Tier 1+2 are heuristics
 * that prevent obvious mistakes and reduce RPC traffic.
 */

/** Whether a Token Program-owned account is a *mint* (defines the
 *  token: supply, decimals, authority) or a *token account* (holds
 *  one user's balance of one mint). Only meaningful when the account
 *  is actually owned by a Token Program — `null` otherwise. */
export type TokenAddressKind = "mint" | "account";

export interface SolanaAccountSummary {
    /** Owning program's base58 address. System Program → bare wallet;
     *  SPL Token Program → SPL Token Account; anything else → a PDA
     *  controlled by that program. */
    owner: string;
    /** Lamport balance. Useful as a sanity signal — a "real" wallet
     *  typically has more than the rent-exempt minimum (~890_880
     *  lamports for a System-owned account with no data). */
    lamports: number;
    /** For Token Program / Token-2022 Program owned accounts only:
     *  whether this is a mint or a token account. Determined from the
     *  account data layout — see `classifyTokenAddress` below. */
    tokenKind?: TokenAddressKind;
    /** When `tokenKind === "account"`, the mint this token account
     *  holds balance for (base58). Decoded from bytes 0–32 of the
     *  account data. Compare against current `USDV_MINT` from
     *  `tokens/usdv` to gate "wrong mint" warnings. */
    tokenMint?: string;
    /** When `tokenKind === "account"`, the SPL Token `state` byte at
     *  data offset 108. Layout is identical for SPL Token and
     *  Token-2022 base structs:
     *    `0` = Uninitialized — created but never `initializeAccount`d.
     *    `1` = Initialized — normal, transfers allowed.
     *    `2` = Frozen — freeze authority has frozen this account,
     *           transfers reject on chain until thawed.
     *  Anything other than `1` means transfers to this account will
     *  fail; consumers should hard-block. */
    tokenAccountState?: number;
}

/** Token Program account data layouts:
 *
 *   SPL Token (legacy):
 *     - Mint:          exactly 82 bytes
 *     - Token Account: exactly 165 bytes
 *
 *   Token-2022 (with extensions):
 *     - Vanilla mint / account match the legacy sizes above.
 *     - Extended accounts pad to >= 166 bytes and reserve byte 165 as
 *       the *account-type discriminator*:
 *         0x01 → Mint
 *         0x02 → Account
 *
 *   So size + one byte tells us unambiguously which one we're looking
 *   at, with no extra RPC needed beyond the `getAccountInfo` we
 *   already make. Caller passes the base64-encoded data string from
 *   the RPC response.
 */
function classifyTokenAddress(dataBase64: string): TokenAddressKind | undefined {
    const data = atob(dataBase64);
    if (data.length === 82) return "mint";
    if (data.length === 165) return "account";
    if (data.length < 165) return undefined;
    const tag = data.charCodeAt(165);
    if (tag === 0x01) return "mint";
    if (tag === 0x02) return "account";
    return undefined;
}

/** Decode the mint address from a Token (or Token-2022) Account's
 *  raw data. The mint lives at bytes 0–32 in both program versions.
 *  Returns `undefined` if the data is too short to contain a mint. */
function extractTokenAccountMint(dataBase64: string): string | undefined {
    const data = atob(dataBase64);
    if (data.length < 32) return undefined;
    const bytes = new Uint8Array(32);
    for (let i = 0; i < 32; i++) bytes[i] = data.charCodeAt(i);
    return new PublicKey(bytes).toBase58();
}

/** Read the SPL Token `state` byte (offset 108) of a token account's
 *  raw data. Same offset for SPL Token and Token-2022 base structs.
 *  Returns `undefined` if the data is too short. */
function extractTokenAccountState(dataBase64: string): number | undefined {
    const data = atob(dataBase64);
    if (data.length <= 108) return undefined;
    return data.charCodeAt(108);
}

export const SolanaAddress = {
    /**
     * Tier 1 — string decodes to base58 and produces exactly 32 bytes.
     *
     * Prefer this over `PublicKey.isValidBase58`, which only checks
     * the string-length window (32–44 chars) without confirming the
     * decoded byte length lands on 32. The looser variant accepts
     * truncated pastes that survive the string-length check but
     * decode to fewer than 32 bytes — a common failure mode for
     * users copy/pasting an address minus a trailing char.
     */
    isShape(address: string): boolean {
        const trimmed = (address ?? "").trim();
        if (!trimmed) return false;
        return PublicKey.isBase58PublicKey(trimmed);
    },

    /**
     * Tier 2 (standalone) — bytes represent a valid Ed25519 curve
     * point. Returns `false` for shape-invalid input rather than
     * throwing, so it's safe to call without first running
     * `isShape`. Useful as a secondary discriminator *after* an
     * on-chain owner is known — e.g. owner = System Program +
     * off-curve = a native System multisig, not a regular wallet.
     */
    isOnCurve(address: string): boolean {
        const trimmed = (address ?? "").trim();
        if (!trimmed) return false;
        if (!PublicKey.isBase58PublicKey(trimmed)) return false;
        try {
            return PublicKey.isOnCurve(trimmed);
        } catch {
            return false;
        }
    },

    /**
     * Tier 1 + Tier 2 — shape + Ed25519 curve membership. Returns
     * `true` only for addresses that could be wallets (a private key
     * could correspond to them). Rejects PDAs and random off-curve
     * garbage indiscriminately — they're indistinguishable by bytes
     * alone, so this is the right strictness for a "must be a
     * wallet" input but the wrong strictness if PDAs are valid.
     */
    isWallet(address: string): boolean {
        return SolanaAddress.isShape(address) && SolanaAddress.isOnCurve(address);
    },

    /**
     * Tier 3 — `getAccountInfo` RPC lookup. Returns `null` when no
     * account exists at the address (the normal "fresh wallet" case
     * for an address that hasn't received funds yet). Returns
     * `{ owner, lamports }` when an account exists.
     *
     * This is a **read**, not a transaction — no signature, no
     * network fee, no wallet connection required. RPC hosts may
     * rate-limit, so callers should:
     *   1. Only invoke after `isShape` (or `isWallet`) passes.
     *   2. Debounce ~300–500ms after the user stops typing.
     *   3. Abort in-flight requests when a newer keystroke
     *      supersedes them (use `AbortController` via the
     *      `ConnectionManagerOptions.signal`).
     *
     * Throws only on RPC errors (network failure, timeout). Callers
     * should `try/catch` to distinguish "lookup failed" from "lookup
     * said no account."
     */
    async fetchAccount(address: string): Promise<SolanaAccountSummary | null> {
        const trimmed = address.trim();
        if (!PublicKey.isBase58PublicKey(trimmed)) return null;
        // Go through the raw RPC client rather than
        // `ConnectionManager.getAccountInfo` to skip its built-in
        // decode pass. The decode step calls into Token / Token-2022
        // / mint deserialization and can throw on accounts with
        // unusual extension data (e.g. USDG's Token-2022 mint with
        // TransferHook / ConfidentialTransfer extensions). We only
        // need owner + lamports for classification — losing the
        // decoded mint/account struct is fine. Throwing here would
        // surface as "Couldn't verify on chain" in the UI even
        // though the chain actually returned valid data.
        const response = await ConnectionManager.getRpcClient().getAccountInfo(trimmed, { encoding: "base64" });
        const info = response?.value;
        // RPC returns `value: null` when no account exists at the
        // address — the canonical "not found" signal.
        if (!info?.owner) return null;
        // Token Program ownership → parse the data layout to figure
        // out mint vs token account, and (for accounts) read the
        // mint the account is bound to. Both pieces are needed to
        // gate "wrong mint" / "this is a mint, not a wallet" errors
        // at the form level without spending another RPC.
        const isTokenProgram = info.owner === TOKEN_PROGRAM_ID_STRING || info.owner === TOKEN_2022_PROGRAM_ID_STRING;
        const dataBase64 = info.data?.[0] ?? null;
        let tokenKind: TokenAddressKind | undefined;
        let tokenMint: string | undefined;
        let tokenAccountState: number | undefined;
        if (isTokenProgram && dataBase64) {
            tokenKind = classifyTokenAddress(dataBase64);
            if (tokenKind === "account") {
                tokenMint = extractTokenAccountMint(dataBase64);
                tokenAccountState = extractTokenAccountState(dataBase64);
            }
        }
        return {
            owner: info.owner,
            lamports: info.lamports,
            tokenKind,
            tokenMint,
            tokenAccountState
        };
    }
} as const;
